import type { LucideIcon } from 'lucide-react';
import {
    AppWindow,
    Bell,
    Bot,
    Box,
    Brain,
    Cable,
    Database,
    FileJson,
    FolderOpen,
    GitBranch,
    KeyRound,
    Link2,
    MessageSquare,
    Plug,
    Puzzle,
    Scale,
    Server,
    Shield,
    Sparkles,
    Tags,
    UserCog,
    Wrench,
} from 'lucide-react';

export type CapabilityType =
    | 'agent'
    | 'tool'
    | 'model'
    | 'workflow'
    | 'prompt'
    | 'skill'
    | 'role'
    | 'memory'
    | 'policy'
    | 'permission'
    | 'schema'
    | 'subscription'
    | 'domain_type'
    | 'application'
    | 'app'
    | 'decision'
    | 'binding'
    | 'sandbox'
    | 'workspace'
    | 'provider'
    | 'worker'
    | 'integration'
    | 'connection';

export interface CapabilityColorConfig {
    border: string;
    bgLight: string;
    text: string;
    hover: string;
    hex: string;
    handleBg: string;
    handleBorder: string;
    icon: LucideIcon;
    label: string;
    description: string;
}

export const capabilityColors: Record<CapabilityType, CapabilityColorConfig> = {
    agent: {
        border: 'border-l-blue-500',
        bgLight: 'bg-blue-100',
        text: 'text-blue-600',
        hover: 'hover:bg-blue-50',
        hex: '#2563eb',
        handleBg: '!bg-blue-500',
        handleBorder: '!border-blue-600',
        icon: Bot,
        label: 'Agents',
        description: 'AI agents',
    },
    tool: {
        border: 'border-l-violet-500',
        bgLight: 'bg-violet-100',
        text: 'text-violet-600',
        hover: 'hover:bg-violet-50',
        hex: '#7c3aed',
        handleBg: '!bg-violet-500',
        handleBorder: '!border-violet-600',
        icon: Wrench,
        label: 'Tools',
        description: 'Function tools',
    },
    model: {
        border: 'border-l-amber-500',
        bgLight: 'bg-amber-100',
        text: 'text-amber-600',
        hover: 'hover:bg-amber-50',
        hex: '#d97706',
        handleBg: '!bg-amber-500',
        handleBorder: '!border-amber-600',
        icon: Brain,
        label: 'Models',
        description: 'LLM configurations',
    },
    workflow: {
        border: 'border-l-indigo-500',
        bgLight: 'bg-indigo-100',
        text: 'text-indigo-600',
        hover: 'hover:bg-indigo-50',
        hex: '#4f46e5',
        handleBg: '!bg-indigo-500',
        handleBorder: '!border-indigo-600',
        icon: GitBranch,
        label: 'Workflows',
        description: 'Process definitions',
    },
    prompt: {
        border: 'border-l-rose-500',
        bgLight: 'bg-rose-100',
        text: 'text-rose-600',
        hover: 'hover:bg-rose-50',
        hex: '#e11d48',
        handleBg: '!bg-rose-500',
        handleBorder: '!border-rose-600',
        icon: MessageSquare,
        label: 'Prompts',
        description: 'Prompt templates',
    },
    skill: {
        border: 'border-l-fuchsia-500',
        bgLight: 'bg-fuchsia-100',
        text: 'text-fuchsia-600',
        hover: 'hover:bg-fuchsia-50',
        hex: '#c026d3',
        handleBg: '!bg-fuchsia-500',
        handleBorder: '!border-fuchsia-600',
        icon: Sparkles,
        label: 'Skills',
        description: 'Agent skills',
    },
    role: {
        border: 'border-l-teal-500',
        bgLight: 'bg-teal-100',
        text: 'text-teal-600',
        hover: 'hover:bg-teal-50',
        hex: '#0d9488',
        handleBg: '!bg-teal-500',
        handleBorder: '!border-teal-600',
        icon: UserCog,
        label: 'Roles',
        description: 'Role definitions',
    },
    memory: {
        border: 'border-l-cyan-600',
        bgLight: 'bg-cyan-100',
        text: 'text-cyan-700',
        hover: 'hover:bg-cyan-50',
        hex: '#0e7490',
        handleBg: '!bg-cyan-600',
        handleBorder: '!border-cyan-700',
        icon: Database,
        label: 'Memory',
        description: 'Durable knowledge stores',
    },
    policy: {
        border: 'border-l-orange-500',
        bgLight: 'bg-orange-100',
        text: 'text-orange-600',
        hover: 'hover:bg-orange-50',
        hex: '#ea580c',
        handleBg: '!bg-orange-500',
        handleBorder: '!border-orange-600',
        icon: Shield,
        label: 'Policies',
        description: 'Access policies',
    },
    permission: {
        border: 'border-l-violet-500',
        bgLight: 'bg-violet-100',
        text: 'text-violet-600',
        hover: 'hover:bg-violet-50',
        hex: '#7c3aed',
        handleBg: '!bg-violet-500',
        handleBorder: '!border-violet-600',
        icon: KeyRound,
        label: 'Permissions',
        description: 'Access permissions',
    },
    schema: {
        border: 'border-l-sky-500',
        bgLight: 'bg-sky-100',
        text: 'text-sky-600',
        hover: 'hover:bg-sky-50',
        hex: '#0284c7',
        handleBg: '!bg-sky-500',
        handleBorder: '!border-sky-600',
        icon: FileJson,
        label: 'Schemas',
        description: 'Data schemas',
    },
    subscription: {
        border: 'border-l-pink-500',
        bgLight: 'bg-pink-100',
        text: 'text-pink-600',
        hover: 'hover:bg-pink-50',
        hex: '#db2777',
        handleBg: '!bg-pink-500',
        handleBorder: '!border-pink-600',
        icon: Bell,
        label: 'Subscriptions',
        description: 'Event subscriptions',
    },
    domain_type: {
        border: 'border-l-emerald-500',
        bgLight: 'bg-emerald-100',
        text: 'text-emerald-600',
        hover: 'hover:bg-emerald-50',
        hex: '#059669',
        handleBg: '!bg-emerald-500',
        handleBorder: '!border-emerald-600',
        icon: Tags,
        label: 'Domain Types',
        description: 'Type definitions',
    },
    application: {
        border: 'border-l-cyan-500',
        bgLight: 'bg-cyan-100',
        text: 'text-cyan-600',
        hover: 'hover:bg-cyan-50',
        hex: '#0891b2',
        handleBg: '!bg-cyan-500',
        handleBorder: '!border-cyan-600',
        icon: AppWindow,
        label: 'Application',
        description: 'Application definition and defaults',
    },
    app: {
        border: 'border-l-cyan-500',
        bgLight: 'bg-cyan-100',
        text: 'text-cyan-600',
        hover: 'hover:bg-cyan-50',
        hex: '#0891b2',
        handleBg: '!bg-cyan-500',
        handleBorder: '!border-cyan-600',
        icon: AppWindow,
        label: 'Apps',
        description: 'Applications',
    },
    decision: {
        border: 'border-l-purple-500',
        bgLight: 'bg-purple-100',
        text: 'text-purple-600',
        hover: 'hover:bg-purple-50',
        hex: '#9333ea',
        handleBg: '!bg-purple-500',
        handleBorder: '!border-purple-600',
        icon: Scale,
        label: 'Decisions',
        description: 'Decision rules',
    },
    binding: {
        border: 'border-l-yellow-500',
        bgLight: 'bg-yellow-100',
        text: 'text-yellow-600',
        hover: 'hover:bg-yellow-50',
        hex: '#ca8a04',
        handleBg: '!bg-yellow-500',
        handleBorder: '!border-yellow-600',
        icon: Link2,
        label: 'Bindings',
        description: 'Capability bindings',
    },
    sandbox: {
        border: 'border-l-lime-500',
        bgLight: 'bg-lime-100',
        text: 'text-lime-600',
        hover: 'hover:bg-lime-50',
        hex: '#65a30d',
        handleBg: '!bg-lime-500',
        handleBorder: '!border-lime-600',
        icon: Box,
        label: 'Sandboxes',
        description: 'Execution sandboxes',
    },
    workspace: {
        border: 'border-l-sky-600',
        bgLight: 'bg-sky-100',
        text: 'text-sky-700',
        hover: 'hover:bg-sky-50',
        hex: '#0284c7',
        handleBg: '!bg-sky-600',
        handleBorder: '!border-sky-700',
        icon: FolderOpen,
        label: 'Workspaces',
        description: 'Storage workspaces',
    },
    provider: {
        border: 'border-l-green-500',
        bgLight: 'bg-green-100',
        text: 'text-green-600',
        hover: 'hover:bg-green-50',
        hex: '#16a34a',
        handleBg: '!bg-green-500',
        handleBorder: '!border-green-600',
        icon: Plug,
        label: 'Providers',
        description: 'LLM providers',
    },
    worker: {
        border: 'border-l-stone-500',
        bgLight: 'bg-stone-100',
        text: 'text-stone-600',
        hover: 'hover:bg-stone-50',
        hex: '#78716c',
        handleBg: '!bg-stone-500',
        handleBorder: '!border-stone-600',
        icon: Server,
        label: 'Workers',
        description: 'Worker processes',
    },
    integration: {
        border: 'border-l-violet-500',
        bgLight: 'bg-violet-100',
        text: 'text-violet-600',
        hover: 'hover:bg-violet-50',
        hex: '#8b5cf6',
        handleBg: '!bg-violet-500',
        handleBorder: '!border-violet-600',
        icon: Puzzle,
        label: 'Integrations',
        description: 'Third-party API blueprints',
    },
    connection: {
        border: 'border-l-emerald-500',
        bgLight: 'bg-emerald-100',
        text: 'text-emerald-600',
        hover: 'hover:bg-emerald-50',
        hex: '#10b981',
        handleBg: '!bg-emerald-500',
        handleBorder: '!border-emerald-600',
        icon: Cable,
        label: 'Connections',
        description: 'Authenticated API sessions',
    },
};

export function getCapabilityColors(type: string): CapabilityColorConfig {
    return capabilityColors[type as CapabilityType] ?? capabilityColors.app;
}

/** Inline background for small header title icons (glass layouts). */
export function capabilityTitleIconBackground(hex: string, intense = false): { backgroundColor: string } {
    const pct = intense ? 30 : 46;
    return { backgroundColor: `color-mix(in srgb, ${hex} ${pct}%, white)` };
}

export const defaultTaskColors: CapabilityColorConfig = {
    border: 'border-l-blue-500',
    bgLight: 'bg-blue-100',
    text: 'text-blue-600',
    hover: 'hover:bg-blue-50',
    hex: '#3b82f6',
    handleBg: '!bg-blue-400',
    handleBorder: '!border-blue-500',
    icon: Bot,
    label: 'Task',
    description: 'Default task',
};

const statusBadgeStyles: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    Active: 'bg-emerald-100 text-emerald-700',
    paused: 'bg-amber-100 text-amber-700',
    Paused: 'bg-amber-100 text-amber-700',
    suspended: 'bg-amber-100 text-amber-700',
    draft: 'bg-amber-100 text-amber-700',
    archived: 'bg-red-100 text-red-700',
    deleted: 'bg-red-100 text-red-700',
    deprecated: 'bg-slate-100 text-slate-600',
    disabled: 'bg-red-100 text-red-700',
};

export function getStatusStyle(status: string): string {
    return statusBadgeStyles[status] ?? 'bg-slate-100 text-slate-600';
}
